export type SportsbookMechanic =
  | "freeBets"
  | "betRefund"
  | "snrFreeBet"
  | "comboBoost";
export type ProviderTemplate = Record<string, any>;

export const sportsbookMechanics: SportsbookMechanic[] = [
  "freeBets",
  "betRefund",
  "snrFreeBet",
  "comboBoost",
];
const subtypeMechanic: Record<string, SportsbookMechanic> = {
  free_money: "freeBets",
  bet_refund: "betRefund",
  snr: "snrFreeBet",
};
const savedFreeBetMechanic: Record<string, SportsbookMechanic> = {
  freebet: "freeBets",
  bet_refund: "betRefund",
  snr: "snrFreeBet",
};

function presentMetadata(values: unknown[]): unknown[] {
  return values.filter((value) => value != null && value !== "");
}

function canonicalBonusType(value: unknown): "freebet" | "comboboost" | "other" {
  if (value === 1 || value === "1") return "freebet";
  if (value === 2 || value === "2") return "comboboost";
  if (typeof value !== "string") return "other";
  const normalized = value.trim().toLowerCase();
  if (normalized === "freebet") return "freebet";
  if (normalized === "comboboost") return "comboboost";
  return "other";
}

function providerNodes(template: ProviderTemplate): ProviderTemplate[] {
  const nested = template.provider_template;
  return nested && typeof nested === "object" ? [nested, template] : [template];
}

/**
 * Classify only provider metadata.
 * Subtype order: sub_type, freebet_data.type, custom_options.freebet_data.type.
 * Contradictory metadata stays unknown. reward_type alone never selects a mechanic.
 */
export function classifySportsbookTemplate(
  template?: ProviderTemplate,
): SportsbookMechanic | undefined {
  if (!template || typeof template !== "object") return undefined;
  const nodes = providerNodes(template);
  const subtypes = Array.from(
    new Set(
      nodes.flatMap((node) =>
        presentMetadata([
          node.sub_type,
          node.freebet_data?.type,
          node.custom_options?.freebet_data?.type,
        ]).map((value) => String(value).trim()),
      ),
    ),
  );
  if (subtypes.length > 1) return undefined;
  const subtype = subtypes[0];
  const bonusTypes = Array.from(
    new Set(
      nodes.flatMap((node) =>
        presentMetadata([node.bonus_type]).map((value) => canonicalBonusType(value)),
      ),
    ),
  );
  if (bonusTypes.length > 1) return undefined;
  const bonusType = bonusTypes[0];
  if (bonusType === "comboboost") return subtype == null ? "comboBoost" : undefined;
  if (bonusType === "other") return undefined;
  if (
    typeof subtype !== "string" ||
    !Object.prototype.hasOwnProperty.call(subtypeMechanic, subtype)
  ) {
    return undefined;
  }
  return subtypeMechanic[subtype];
}

/** Visible dropdown text: API label, then provider title. Never invented from the template id. */
export function sportsbookTemplateLabel(
  label: unknown,
  template?: ProviderTemplate,
): string {
  const text = (value: unknown) =>
    typeof value === "string" && value.trim() !== "" ? value.trim() : "";
  const primary = text(label);
  if (primary) return primary;
  const nodes = template ? providerNodes(template) : [];
  return nodes.map((node) => text(node.title)).find(Boolean) ?? "";
}

export function resolveSportsbookTemplateSelection(
  templateId: string | undefined,
  options: { value: string; label: string }[],
  fetchReady: boolean,
  legacyTemplateId?: string,
) {
  const option = templateId
    ? options.find((item) => item.value === String(templateId))
    : undefined;
  if (option) {
    return { option, unavailable: false, label: option.label || undefined };
  }
  const absent = fetchReady && Boolean(templateId);
  return {
    option: undefined,
    unavailable: Boolean(
      absent && legacyTemplateId && legacyTemplateId === templateId,
    ),
    label: absent ? String(templateId) : undefined,
  };
}

export const freeBetMechanics: SportsbookMechanic[] = [
  "freeBets",
  "betRefund",
  "snrFreeBet",
];
export const isFreeBetMechanic = (type?: string): boolean =>
  freeBetMechanics.includes(type as SportsbookMechanic);

export function freeBetRewardConfig(
  reward: {
    type?: string;
    templateId?: string;
    providerTemplate?: ProviderTemplate;
    persistedConfig?: Record<string, any>;
    legacyTemplateId?: string;
    amount?: string;
    maxWin?: string;
    winningsDestination?: string;
  },
  currency: string,
) {
  const mechanic = classifySportsbookTemplate(reward.providerTemplate);
  const retainedLegacy =
    reward.type === "freeBets" &&
    !mechanic &&
    reward.legacyTemplateId === reward.templateId &&
    Boolean(reward.templateId);
  if (!reward.templateId || (!retainedLegacy && mechanic !== reward.type)) {
    throw new Error("sportsbookTemplateMismatch");
  }
  return {
    ...reward.persistedConfig,
    provider: reward.persistedConfig?.provider || "slotegrator",
    templateId: Number(reward.templateId),
    providerTemplate: reward.providerTemplate,
    amount: Number(reward.amount),
    maxWin: Number(reward.maxWin),
    winningsDestination:
      reward.winningsDestination === "real"
        ? ("real" as const)
        : ("bonus" as const),
    currency,
  };
}

export function hydrateFreeBetMechanic(
  templateId: string,
  template?: ProviderTemplate,
  savedType?: string,
) {
  const mechanic = classifySportsbookTemplate(template);
  if (isFreeBetMechanic(mechanic)) {
    return { type: mechanic, legacyTemplateId: undefined as string | undefined };
  }
  return {
    type: (savedType && savedFreeBetMechanic[savedType]) || "freeBets",
    legacyTemplateId: templateId,
  };
}

/** One write per exact mechanic; an explicit delete also clears an empty composition. */
export function freeBetRewardWrites(
  rewards: Parameters<typeof freeBetRewardConfig>[0][],
  currency: string,
): { type: string; config?: string }[] {
  const types: Record<string, string> = { freeBets: "freebet", betRefund: "bet_refund", snrFreeBet: "snr" };
  const seen = new Set<string>();
  const writes: { type: string; config?: string }[] = rewards.filter((reward) => isFreeBetMechanic(reward.type)).map((reward) => {
    const type = types[reward.type!];
    if (seen.has(type)) throw new Error("sportsbookTemplateMismatch");
    seen.add(type);
    return { type, config: JSON.stringify(freeBetRewardConfig(reward, currency)) };
  });
  Object.values(types).forEach((type) => {
    if (!seen.has(type)) writes.push({ type });
  });
  return writes;
}
